import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styles from '../index.module.css'

const EnhancedSelect = ({
  options,
  placeholder,
  searchPlaceholder,
  selectedOptions,
  defaultOption,
  onToggleOption,
  onOptionSelect,
  onSearch,
  label,
  subtitle,
  isRequired = false,
  disabled = false,
  isLoading = false,
  error = null,
  searchable = true,
  multiSelect = true
}) => {
  // Convert single selection to array format for internal consistency
  const [internalSelectedOptions, setInternalSelectedOptions] = useState(() => {
    if (!multiSelect && defaultOption) {
      // For single select, convert defaultOption to our internal format
      const option = typeof defaultOption === 'string' 
        ? { value: defaultOption, label: defaultOption }
        : defaultOption;
      return [option];
    }
    return selectedOptions || [];
  });
  
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const selectRef = useRef(null)

  // Format and filter options to ensure consistent object format
  const formattedOptions = options.map(option => 
    typeof option === 'string' ? { value: option, label: option } : option
  );
  
  const filteredOptions = searchTerm.trim() === '' 
    ? formattedOptions 
    : formattedOptions.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (multiSelect) {
      // Multi-select behavior: toggle the selected option
      const isSelected = internalSelectedOptions.some(
        selected => selected.value === option.value
      );
      
      let newSelectedOptions;
      if (isSelected) {
        newSelectedOptions = internalSelectedOptions.filter(
          selected => selected.value !== option.value
        );
      } else {
        newSelectedOptions = [...internalSelectedOptions, option];
      }
      
      setInternalSelectedOptions(newSelectedOptions);
      
      if (onToggleOption) {
        onToggleOption(option);
      }
    } else {
      // Single-select behavior: replace the selection
      setInternalSelectedOptions([option]);
      
      if (onOptionSelect) {
        onOptionSelect(option);
      }
      
      setIsOpen(false);
    }
    
    setSearchTerm('');
  }

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value
    setSearchTerm(term)
    if (onSearch) {
      onSearch(term)
    }
  }

  // Close the dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!multiSelect && defaultOption) {
      const option = typeof defaultOption === 'string' 
        ? { value: defaultOption, label: defaultOption }
        : defaultOption;
      setInternalSelectedOptions([option]);
    } else if (selectedOptions) {
      setInternalSelectedOptions(selectedOptions);
    }
  }, [defaultOption, selectedOptions, multiSelect]);

  // Get display text for the selection
  const getSelectionDisplay = () => {
    if (internalSelectedOptions.length === 0) {
      return null;
    } else if (!multiSelect || internalSelectedOptions.length === 1) {
      return internalSelectedOptions[0].label;
    } else {
      return `${internalSelectedOptions.length} items selected`;
    }
  }

  const selectionDisplay = getSelectionDisplay();

  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {isRequired && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      
      <div className={styles.formRow}>
        <div className={styles.selectContainer} ref={selectRef}>
          <div 
            className={`${styles.inputField} ${styles.selectButton}`}
            onClick={toggleDropdown}
          >
            {selectionDisplay || <span className={styles.placeholder}>{placeholder || "Select options"}</span>}
            <span className={styles.selectArrow}>▼</span>
          </div>
          
          {isOpen && (
            <div className={styles.selectDropdown}>
              {searchable && (
                <div className={styles.searchContainer}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={searchPlaceholder || "Search..."}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              )}
              
              <div className={styles.optionsContainer}>
                {isLoading ? (
                  <div className={styles.loadingOption}>Loading...</div>
                ) : filteredOptions.length === 0 ? (
                  <div className={styles.noResults}>No options found</div>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = internalSelectedOptions.some(
                      selected => selected.value === option.value
                    );
                    
                    return (
                      <div
                        key={index}
                        className={`${multiSelect ? styles.multiOption : styles.option} ${isSelected ? styles.selectedOption : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionSelect(option);
                        }}
                      >
                        {multiSelect && (
                          <input
                            type="checkbox"
                            className={styles.optionCheckbox}
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <span className={styles.optionLabel}>{option.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
              
              {multiSelect && internalSelectedOptions.length > 0 && (
                <div className={styles.selectedCount}>
                  {internalSelectedOptions.length} selected
                </div>
              )}
            </div>
          )}
        </div>
        
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
      
      {/* Selected options display - only for multi-select */}
      {multiSelect && internalSelectedOptions.length > 0 && (
        <div className={styles.selectedOptionsContainer}>
          {internalSelectedOptions.map((option, index) => (
            <div key={index} className={styles.faqCardLink}>
              {option.label}
              <button
                type="button"
                className={styles.removeOption}
                onClick={() => handleOptionSelect(option)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

EnhancedSelect.propTypes = {
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  defaultOption: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ]),
  onToggleOption: PropTypes.func,
  onOptionSelect: PropTypes.func,
  onSearch: PropTypes.func,
  label: PropTypes.string,
  subtitle: PropTypes.string,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  searchable: PropTypes.bool,
  multiSelect: PropTypes.bool
}

export default EnhancedSelect